import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/app/components/ui/sonner';

// ─── Lazy page imports ────────────────────────────────────────────────────────
// Each page is split into its own chunk. Vite tree-shakes aggressively so the
// initial bundle only contains routing + auth shell code.
const LoginPage = lazy(() => import('@/app/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('@/app/pages/SignupPage').then((m) => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() => import('@/app/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/app/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const PublicPetPassportPage = lazy(() => import('@/app/pages/PublicPetPassportPage').then((m) => ({ default: m.PublicPetPassportPage })));
const EmailConfirmationHandler = lazy(() => import('@/app/components/EmailConfirmationHandler').then((m) => ({ default: m.EmailConfirmationHandler })));
const ReferralRedirect = lazy(() => import('@/app/components/ReferralRedirect').then((m) => ({ default: m.ReferralRedirect })));
const ClaimPetPage = lazy(() => import('@/app/pages/ClaimPetPage').then((m) => ({ default: m.ClaimPetPage })));
const AuthenticatedAppShell = lazy(() =>
  import('@/app/AuthenticatedAppShell').then((m) => ({ default: m.AuthenticatedAppShell }))
);

// ─── Page loader fallback ─────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function RedirectToLoginWithNext() {
  const location = useLocation();
  const nextPath = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
}

function PublicRoutesNoAuth() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/passport/:passportId" element={<PublicPetPassportPage />} />
        <Route path="/email-confirmed" element={<EmailConfirmationHandler />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/r/:code" element={<ReferralRedirect />} />
        <Route path="/claim/:passportId" element={<ClaimPetPage />} />
        <Route path="/" element={<RedirectToLoginWithNext />} />
        <Route path="*" element={<RedirectToLoginWithNext />} />
      </Routes>
    </Suspense>
  );
}

function hasStoredSupabaseSession() {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.localStorage.getItem('sb-auth-token'));
  } catch {
    return false;
  }
}

function requiresAuthBootstrap(pathname: string) {
  // Auth callbacks can mint a session from URL state before localStorage is written.
  if (pathname === '/email-confirmed' || pathname === '/reset-password') return true;
  return hasStoredSupabaseSession();
}

function AppBootstrap() {
  const location = useLocation();
  const bootAuth = requiresAuthBootstrap(location.pathname);

  if (!bootAuth) {
    return (
      <>
        <Toaster position="top-right" />
        <PublicRoutesNoAuth />
      </>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <Suspense fallback={<PageLoader />}>
        <AuthenticatedAppShell />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppBootstrap />
    </BrowserRouter>
  );
}

export default App;
